import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { grey } from "@mui/material/colors";
import { useState } from "react";

export const GROWTH_METRICS = [
	{
		id: "latest_vs_max",
		name: "Last month vs max",
		field: "growthLatestVsMax",
		help: "Latest month compared with the highest earlier month. Negative means last month is below the peak.",
	},
	{
		id: "yoy",
		name: "Year over year",
		field: "growthYoY",
		help: "Latest month compared with the same month a year earlier.",
	},
	{
		id: "mom",
		name: "Month over month",
		field: "growthMoM",
		help: "Latest month compared with the previous month.",
	},
	{
		id: "latest_vs_avg",
		name: "Last month vs average",
		field: "growthLatestVsAvg",
		help: "Latest month compared with the average of all months in the history.",
	},
	{
		id: "three_months_vs_avg",
		name: "Last 3 months vs prev avg",
		field: "growthThreeMonthsVsAvg",
		help: "Average of the last 3 months compared with the average of earlier months. Useful for a trend, not a one-month spike.",
	},
];

export const COLUMN_HELP = {
	text: "Search term from Google Ads Keyword Planner for this run’s seeds, country, and language.",
	latestSearchVolume: "Searches in the most recent full month. Keyword Planner historical metrics, not live traffic.",
	avgMonthlySearches: "Average monthly searches over the Planner lookback window (about 2 years).",
	growthYoY: "Latest month vs the same month a year ago. Green is up, red is down.",
	growthMoM: "Latest month vs the previous month. Green is up, red is down.",
	growthLatestVsAvg: "Latest month vs the average of all months in the history.",
	growthLatestVsMax: "Latest month vs the highest earlier month. Shows whether last month is a peak.",
	growthThreeMonthsVsAvg: "Average of the last 3 months vs the average of earlier months.",
	competition: "Google’s competition bucket: Low, Medium, or High. The API stores this as 2 / 3 / 4 — we show the label, not the number. This is not the 0–100 index.",
	competitionIndex: "A 0–100 score of how many advertisers bid on this keyword. Higher = more competitive. A 4 here means 4/100, not High.",
	lowTopOfPageBid: "Low end of the top-of-page bid range from Keyword Planner, in account currency.",
	highTopOfPageBid: "High end of the top-of-page bid range from Keyword Planner, in account currency.",
	averageCpc: "Average CPC from Keyword Planner historical metrics, in account currency.",
	closeVariants: "Close-variant keywords returned with this idea by Keyword Planner.",
};

const GUIDE_SECTIONS = [
	{
		title: "Volume",
		rows: [
			["Latest vol", COLUMN_HELP.latestSearchVolume],
			["Avg monthly", COLUMN_HELP.avgMonthlySearches],
			["Cluster volume", "Combined search volume of the keywords in that topic. Bubble size uses this."],
		],
	},
	{
		title: "Growth (shown as %)",
		rows: GROWTH_METRICS.map((item) => [item.name, item.help]),
	},
	{
		title: "Other",
		rows: [
			["Competition", COLUMN_HELP.competition],
			["Comp. index (0–100)", COLUMN_HELP.competitionIndex],
			["Low / high bid", "Top-of-page bid range from Keyword Planner, converted from micros."],
			["Avg CPC", COLUMN_HELP.averageCpc],
			["Keyword", COLUMN_HELP.text],
			["Bubble chart", "X is volume, Y is the growth metric you picked, size is volume. Click a bubble to open that cluster."],
		],
	},
];

export const InfoTip = ({ title }) => (
	<Tooltip
		title={
			<Typography fontSize={12.5} lineHeight={1.5} sx={{ p: 0.25 }}>
				{title}
			</Typography>
		}
		arrow
		placement="top">
		<InfoOutlinedIcon
			sx={{ fontSize: 14, color: grey[500], cursor: "help", flexShrink: 0 }}
			onClick={(event) => event.stopPropagation()}
		/>
	</Tooltip>
);

export const headerWithInfo = (info) =>
	function HeaderWithInfo({ headerText, children }) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 1,
					boxSizing: "border-box",
					gap: 0.5,
				}}>
				<Stack direction="row" alignItems="center" spacing={0.5} minWidth={0} flex={1}>
					<Typography variant="subtitle2" fontWeight={700} fontSize={12.5} noWrap color={grey[900]}>
						{headerText}
					</Typography>
					{info ? <InfoTip title={info} /> : null}
				</Stack>
				{children}
			</Box>
		);
	};

export const MetricGuideButton = ({ sx }) => {
	const [open, setOpen] = useState(false);
	return (
		<>
			<Button startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => setOpen(true)} sx={sx}>
				Metrics guide
			</Button>
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
				<DialogTitle sx={{ fontWeight: 700, pb: 1 }}>What these numbers mean</DialogTitle>
				<DialogContent sx={{ pt: 0 }}>
					<Typography variant="body2" color="text.secondary" mb={2} sx={{ lineHeight: 1.6 }}>
						Search volume comes from Google Ads Keyword Planner (previous full months). Growth is a ratio shown as a
						percent: +20% means 0.20 higher than the comparison period.
					</Typography>
					{GUIDE_SECTIONS.map((section) => (
						<Box key={section.title} mb={2}>
							<Typography fontWeight={700} fontSize={13} mb={0.75}>
								{section.title}
							</Typography>
							{section.rows.map(([label, help]) => (
								<Box
									key={label}
									sx={{
										display: "grid",
										gridTemplateColumns: "140px 1fr",
										gap: 1,
										py: 0.85,
										borderBottom: `1px solid ${grey[100]}`,
									}}>
									<Typography variant="body2" fontWeight={600} color={grey[800]}>
										{label}
									</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
										{help}
									</Typography>
								</Box>
							))}
						</Box>
					))}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
